import React from 'react'
import { Grid } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { isTranslationAvailable } from '@fs/zion-locale'
import UploadGedcomCard from './UploadGedcomCard'
import { type ManageTreesSectionProps } from './manageTreesSectionProps'
import ManageTreesTable from './ManageTreesTable'

/**
 * The new Manage Trees layout: owned tree and shared trees are now combined to a single table.
 * Rendered when `treeWeb_manageTrees_combinedLists` is on.
 */

export default function ManageTreesCombinedTables({
  canAccessFGT,
  failedFiles,
  familyGroupTreeGroups,
  isMobile,
  ownedTrees,
  processingFiles,
  sharedTrees,
  showTreesYouOwn,
  showUploadGedcomCard,
  hasUserTrees,
}: ManageTreesSectionProps): JSX.Element {
  const { t } = useTranslation()

  return (
    <Grid gutters="xs">
      {/* 1. User trees You have - Show upload card or user trees */}
      {/* Unlike the previous design, a shared tree counts as having a tree, so the card gives way to
          the table and its upload button rather than sitting above it. */}
      {showTreesYouOwn && showUploadGedcomCard && !hasUserTrees && <UploadGedcomCard />}

      {(hasUserTrees || processingFiles.length > 0 || failedFiles.length > 0) && (
        <ManageTreesTable
          caption={
            isTranslationAvailable('tree-import.manage-trees.user-trees-heading')
              ? t('tree-import.manage-trees.user-trees-heading')
              : undefined
          }
          groups={[...ownedTrees, ...sharedTrees]}
          isMobile={isMobile}
          processingFiles={processingFiles}
          failedFiles={failedFiles}
          showUploadButton
        />
      )}

      {/* 2. Groups With Trees - Show if user has FGT groups */}
      {/* No processing or failed files here: a GEDCOM is uploaded into a tree of your own, never into a
          family group, so those rows belong to the table above and would otherwise be listed twice. */}
      {canAccessFGT && familyGroupTreeGroups.length > 0 && (
        <ManageTreesTable
          groups={familyGroupTreeGroups}
          isMobile={isMobile}
          title={t('tree-import.manage-trees.family-groups-with-trees-heading')}
          isFamilyGroupTree
        />
      )}
    </Grid>
  )
}
